<template>
    <div id="app" :class="themeClass">
        <router-view />
    </div>
</template>

<script>
export default {
    name: 'App',
    computed: {
        themeClass() {
            return this.$store.getters.isDarkTheme ? 'theme-dark' : 'theme-light';
        }
    },
    watch: {
        '$store.state.theme': {
            immediate: true,
            handler(theme) {
                document.documentElement.setAttribute('data-theme', theme);
                // Toggle theme classes without removing other classes (e.g. chart-active)
                document.body.classList.remove('theme-dark', 'theme-light');
                document.body.classList.add(theme === 'dark' ? 'theme-dark' : 'theme-light');
            }
        }
    }
};
</script>

<style>
/* ═══════════════════════════════════════════
   CSS CUSTOM PROPERTIES — THEME SYSTEM
   ═══════════════════════════════════════════ */

:root,
[data-theme="light"] {
    /* Backgrounds */
    --tc-bg-primary: #ffffff;
    --tc-bg-secondary: #f8f9fd;
    --tc-bg-tertiary: #f0f3fa;
    --tc-bg-elevated: #ffffff;
    --tc-bg-hover: rgba(0, 0, 0, 0.04);
    --tc-bg-active: rgba(0, 0, 0, 0.08);

    /* Text */
    --tc-text-primary: #131722;
    --tc-text-secondary: #787b86;
    --tc-text-tertiary: #9ca3af;
    --tc-text-inverse: #ffffff;

    /* Borders */
    --tc-border-primary: #e0e3eb;
    --tc-border-secondary: #f0f3fa;
    --tc-border-hover: #c8ccd8;

    /* Accent */
    --tc-accent: #2962ff;
    --tc-accent-hover: #1e50d8;
    --tc-accent-bg: rgba(41, 98, 255, 0.1);
    --tc-green: #22c55e;
    --tc-green-hover: #16a34a;
    --tc-green-bg: rgba(34, 197, 94, 0.1);
    --tc-red: #ef4444;
    --tc-red-bg: rgba(239, 68, 68, 0.1);

    /* Chart */
    --tc-chart-bg: #ffffff;
    --tc-chart-grid: #e0e3eb;
    --tc-chart-text: #131722;

    /* Shadows */
    --tc-shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.08);
    --tc-shadow-md: 0 4px 12px rgba(0, 0, 0, 0.1);
    --tc-shadow-lg: 0 8px 30px rgba(0, 0, 0, 0.12);

    /* Nav */
    --tc-nav-bg: #ffffff;
    --tc-nav-border: #e0e3eb;

    /* Cards */
    --tc-card-bg: #ffffff;
    --tc-card-border: #e0e3eb;
    --tc-card-hover: #f8f9fd;

    /* Input */
    --tc-input-bg: #ffffff;
    --tc-input-border: #e0e3eb;
    --tc-input-text: #131722;
    --tc-input-placeholder: #9ca3af;

    /* Scrollbar */
    --tc-scrollbar-track: transparent;
    --tc-scrollbar-thumb: #d1d5db;
    --tc-scrollbar-thumb-hover: #9ca3af;
}

[data-theme="dark"] {
    /* Backgrounds */
    --tc-bg-primary: #121826;
    --tc-bg-secondary: #161c2a;
    --tc-bg-tertiary: #1e2436;
    --tc-bg-elevated: #1a2035;
    --tc-bg-hover: rgba(255, 255, 255, 0.06);
    --tc-bg-active: rgba(255, 255, 255, 0.1);

    /* Text */
    --tc-text-primary: #d1d4dc;
    --tc-text-secondary: #787b86;
    --tc-text-tertiary: #6b7280;
    --tc-text-inverse: #121826;

    /* Borders */
    --tc-border-primary: rgba(55, 59, 75, 0.6);
    --tc-border-secondary: #2f3240;
    --tc-border-hover: #4a4e5e;

    /* Accent */
    --tc-accent: #5b9cf6;
    --tc-accent-hover: #4a8be5;
    --tc-accent-bg: rgba(91, 156, 246, 0.15);
    --tc-green: #22c55e;
    --tc-green-hover: #16a34a;
    --tc-green-bg: rgba(34, 197, 94, 0.15);
    --tc-red: #ef4444;
    --tc-red-bg: rgba(239, 68, 68, 0.15);

    /* Chart */
    --tc-chart-bg: #121826;
    --tc-chart-grid: rgba(55, 59, 75, 0.6);
    --tc-chart-text: #d1d4dc;

    /* Shadows */
    --tc-shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.3);
    --tc-shadow-md: 0 4px 12px rgba(0, 0, 0, 0.4);
    --tc-shadow-lg: 0 8px 30px rgba(0, 0, 0, 0.5);

    /* Nav */
    --tc-nav-bg: #121826;
    --tc-nav-border: rgba(55, 59, 75, 0.6);

    /* Cards */
    --tc-card-bg: #1a2035;
    --tc-card-border: rgba(55, 59, 75, 0.6);
    --tc-card-hover: #1e2436;

    /* Input */
    --tc-input-bg: #1e2436;
    --tc-input-border: #2f3240;
    --tc-input-text: #d1d4dc;
    --tc-input-placeholder: #6b7280;

    /* Scrollbar */
    --tc-scrollbar-track: transparent;
    --tc-scrollbar-thumb: #3f4555;
    --tc-scrollbar-thumb-hover: #5a5f70;
}

html, body {
    margin: 0;
    padding: 0;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background: var(--tc-bg-primary);
    color: var(--tc-text-primary);
    transition: background-color 0.2s ease, color 0.2s ease;
}

/* Global scrollbar theming */
::-webkit-scrollbar {
    width: 6px;
}
::-webkit-scrollbar-track {
    background: var(--tc-scrollbar-track);
}
::-webkit-scrollbar-thumb {
    background: var(--tc-scrollbar-thumb);
    border-radius: 3px;
}
::-webkit-scrollbar-thumb:hover {
    background: var(--tc-scrollbar-thumb-hover);
}

/* Theme toggle button component styles */
.theme-toggle-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border: 1px solid var(--tc-border-primary);
    border-radius: 8px;
    background: var(--tc-bg-secondary);
    color: var(--tc-text-secondary);
    cursor: pointer;
    transition: all 0.2s ease;
    padding: 0;
}
.theme-toggle-btn:hover {
    background: var(--tc-bg-hover);
    border-color: var(--tc-border-hover);
    color: var(--tc-text-primary);
}
</style>
